import React, { useCallback, useState } from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import Typography from '@ui/Typography';
import DestructiveDialogContent from '../../DestructiveDialogContent';
import { CheckboxInput } from '../../Inputs';
import useUpdateUserSettings from '../../AccountUserSettings/useUpdateUserSettings';
import {
  EventName,
  useEventsContext,
} from '../../../context/events/EventsContext';

interface NavigationConfirmationFormProps {
  onConfirm: () => void;
  onSuccess: () => void;
}

const useStyles = makeStyles({ name: 'NavigationConfirmationForm' })(theme => ({
  doNotShowAgainLabel: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: theme.spacing(2),
    gap: theme.spacing(0.5),
  },
}));

const NavigationConfirmationForm = (props: NavigationConfirmationFormProps) => {
  const [doNotShowAgainChecked, setDoNotShowAgainChecked] = useState(false);
  const { updateUserSettings } = useUpdateUserSettings({
    onError: error => {},
  });
  const { onConfirm, onSuccess } = props;
  const { classes } = useStyles();
  const [eventTracking] = useEventsContext();

  const handleSubmit = useCallback(() => {
    if (doNotShowAgainChecked) {
      updateUserSettings({
        variables: {
          input: {
            doNotShowNavigationConfirmationModal: true,
          },
        },
      });
    }

    eventTracking.track({
      type: EventName.DoNotShowNavigationConfirmationModalChanged,
      data: {
        value: doNotShowAgainChecked,
      },
    });

    eventTracking.track({
      type: EventName.ContinueWithUnsavedPreviews,
    });

    onConfirm();
  }, [doNotShowAgainChecked, eventTracking, onConfirm, updateUserSettings]);

  const handleCancel = () => {
    if (typeof onSuccess === 'function') {
      onSuccess();
    }
  };

  const body = (
    <>
      {`You have unsaved previews that will be cleared unless saved to your clips.`}
      <Box display="flex" alignItems="center" flexGrow={1}>
        <label
          htmlFor="do_not_show_again"
          className={classes.doNotShowAgainLabel}
        >
          <CheckboxInput
            id={`do_not_show_again`}
            checked={doNotShowAgainChecked}
            disabled={false}
            onChange={event => setDoNotShowAgainChecked(event.target.checked)}
            label="Don't ask me again"
          />
          <Typography variant="pSm">{"Don't ask me again"}</Typography>
        </label>
      </Box>
    </>
  );
  return (
    <DestructiveDialogContent
      title={'Continue with unsaved previews?'}
      body={body}
      onAction={handleSubmit}
      onCancel={handleCancel}
      actionText="Continue"
      doNotShowAgainCTA
    />
  );
};

export default NavigationConfirmationForm;
